import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {v4 as uuid} from 'uuid'
import {useCashFlow} from '../contexts/CashFlowContext'
import {useReconciliation} from '../contexts/ReconciliationContext'
import {bankRequest, MatchSuggestion, ReconciliationBill} from '../reconciliationApi'
import {BankTransaction} from '../types'
import {addFrequency} from '../../../utils/recurrence'
import {BillsProvider} from '../../bills/contexts/BillsContext'
import {BillViewModal} from '../../bills/components/BillViewModal'
import {UploadCsvModal} from './UploadCsvModal'
import {getCategoryLabel} from '../utils/categories'
import './ReconcileView.scss'
import {NewBankBillPanel} from './NewBankBillPanel'
import {BankTransactionFeed} from './BankTransactionFeed'
import {useBankDismissal} from '../hooks/useBankDismissal'
import {
  billCredits,
  canMatchBillAmount,
  NewBankBill,
  nextGenerationAfterPayment,
} from '../utils/bankBillWorkflow'

const money = (n: number) =>
  new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD'}).format(n)
const day = (s: string) =>
  s
    ? new Date(`${s}T12:00:00`).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'
const pending = (t: BankTransaction) => !t.reconciliationId && !t.reviewedAt
const anzAccount = (id: string) => id.startsWith('anz')

export function ReconciliationSummary({onReconcile}: {onReconcile: () => void}) {
  const {transactions, batches} = useCashFlow()
  const {data, error, loading, refresh} = useReconciliation()
  const anz = transactions.filter((t) => anzAccount(t.accountId))
  const unresolved = anz.filter(pending)
  const latest = batches
    .filter((b) => !b.reverted && (!b.state || b.state === 'complete') && anzAccount(b.accountId))
    .map((b) => b.dateRangeEnd)
    .sort()
    .pop()
  const outstanding = data.bills
    .filter((b) => ['APPROVED', 'SENT'].includes(b.status))
    .reduce((n, b) => n + (b.amountDue || 0), 0)
  return (
    <section className='bank-summary' aria-label='Reconciliation overview'>
      <div>
        <span>Latest ANZ import</span>
        <strong>{latest ? day(latest) : 'No statements yet'}</strong>
        <small>Across your ANZ accounts</small>
      </div>
      <button onClick={onReconcile}>
        <span>Left to reconcile</span>
        <strong>{unresolved.length}</strong>
        <small>
          {money(unresolved.filter((t) => t.amount < 0).reduce((n, t) => n - t.amount, 0))} in
          outgoing transactions
        </small>
      </button>
      <div>
        <span>Bills outstanding</span>
        <strong>{error ? 'Unavailable' : loading ? 'Loading…' : money(outstanding)}</strong>
        <small>All business bills · recorded balances</small>
      </div>
      <div>
        <span>Bank evidence linked</span>
        <strong>{anz.filter((t) => t.reconciliationId).length}</strong>
        <small>Payments connected to their bills</small>
      </div>
      {error && (
        <p className='bank-error' role='alert'>
          {error} <button onClick={() => refresh()}>Retry</button>
        </p>
      )}
    </section>
  )
}

type Allocation = {
  billId: string
  amount: string
  paymentId: string
  acknowledge: boolean
  matchAmount?: boolean
  dateChoice?: 'keep' | 'align'
}
function ReconcileDetail({
  transaction: t,
  onDone,
  openBill,
}: {
  transaction: BankTransaction
  onDone: (accounting?: boolean) => Promise<void>
  openBill: (b: ReconciliationBill) => void
}) {
  const {data, error: overviewError} = useReconciliation()
  const {setCategory, setScope} = useCashFlow()
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]),
    [matching, setMatching] = useState(true)
  const [allocations, setAllocations] = useState<Allocation[]>([]),
    [schedules, setSchedules] = useState<Record<string, string>>({})
  const [search, setSearch] = useState(''),
    [note, setNote] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('')
  const [reviewOpen, setReviewOpen] = useState(false)
  const [newBill, setNewBill] = useState<NewBankBill | null>(null)
  // Retry the same intent with the same key; a changed allocation or reversal starts a new intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestId = useMemo(() => uuid(), [allocations, schedules, newBill, t.reconciliationId])
  const reconciliation = data.reconciliations.find((r) => r.id === t.reconciliationId)
  useEffect(() => {
    let cancelled = false
    if (
      t.amount >= 0 ||
      t.category === 'internal_transfer' ||
      t.scope === 'personal' ||
      !!t.reconciliationId ||
      !!t.reviewedAt
    ) {
      setSuggestions([])
      setMatching(false)
      return
    }
    setMatching(true)
    bankRequest('suggest', {transactionId: t.id})
      .then((r) => {
        if (!cancelled) setSuggestions(r.suggestions)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setMatching(false)
      })
    return () => {
      cancelled = true
    }
  }, [t.id, t.amount, t.category, t.scope, t.reconciliationId, t.reviewedAt])
  const unlinkedPayments = (billId: string) =>
    data.payments.filter((p) => p.documentId === billId && p.amount > 0 && !p.bankReconciliationId)
  const addBill = (bill: ReconciliationBill, paymentId = '') => {
    if (allocations.some((a) => a.billId === bill.id)) return
    const payment = data.payments.find((p) => p.id === paymentId)
    const remaining = Math.max(
      0,
      -t.amount - allocations.reduce((n, a) => n + Number(a.amount || 0), 0)
    )
    setAllocations((a) => [
      ...a,
      {
        billId: bill.id,
        paymentId,
        amount: (
          payment?.amount ||
          (canMatchBillAmount(bill) ? remaining : Math.min(bill.amountDue || 0, remaining))
        ).toFixed(2),
        acknowledge: false,
        matchAmount:
          !payment &&
          canMatchBillAmount(bill) &&
          Math.round((bill.amountDue || 0) * 100) !== Math.round(remaining * 100),
      },
    ])
  }
  const update = (billId: string, patch: Partial<Allocation>) =>
    setAllocations((a) => a.map((v) => (v.billId === billId ? {...v, ...patch} : v)))
  const total = newBill
    ? Math.round(-t.amount * 100)
    : allocations.reduce((n, a) => n + Math.round(Number(a.amount || 0) * 100), 0)
  const balanced = total === Math.round(-t.amount * 100) && (!!newBill || allocations.length > 0)
  const invalidNewBill =
    newBill &&
    (!newBill.contactName.trim() ||
      !newBill.description.trim() ||
      !newBill.taxType ||
      (newBill.recurring &&
        (!newBill.nextDate || newBill.nextDate <= data.asOf || newBill.nextDate <= t.date)))
  const needsDateChoice = (_a: Allocation, b: ReconciliationBill) =>
    !!b.date &&
    b.date !== t.date &&
    data.templates.some((tpl) => tpl.id === b.repeatingTemplateId && tpl.status === 'ACTIVE')
  const unansweredDate = allocations.some((a) => {
    const b = data.bills.find((b) => b.id === a.billId)
    return b && needsDateChoice(a, b) && !a.dateChoice
  })
  const hasUnacknowledged = allocations.some(
    (a) => !a.paymentId && unlinkedPayments(a.billId).length > 0 && !a.acknowledge
  )
  const selectedTemplates = data.templates.filter((tpl) =>
    allocations.some(
      (a) => data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId === tpl.id
    )
  )
  const run = async (action: string, payload: any, message: string) => {
    setBusy(true)
    setError('')
    try {
      await bankRequest(action, payload)
      toast.success(message)
      await onDone(action !== 'review')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }
  const results = search.trim()
    ? data.bills
        .filter(
          (b) =>
            !['VOIDED', 'DRAFT'].includes(b.status) &&
            `${b.contactName} ${b.billNumber} ${b.reference}`
              .toLowerCase()
              .includes(search.toLowerCase())
        )
        .slice(0, 25)
    : (suggestions
        .map((s) => data.bills.find((b) => b.id === s.billId))
        .filter(Boolean) as ReconciliationBill[])
  return (
    <div className='bank-detail'>
      <header>
        <span className='bank-eyebrow'>ANZ statement line · {day(t.date)}</span>
        <h3>{t.descriptionRaw}</h3>
        <strong className={t.amount < 0 ? 'out' : 'in'}>{money(t.amount)}</strong>
        <p>
          {getCategoryLabel(t.category)} · {t.scope || 'Books unassigned'}
        </p>
      </header>
      {(error || overviewError) && (
        <p role='alert' className='bank-error'>
          {error || overviewError}
        </p>
      )}
      {t.reconciliationId ? (
        <section className='bank-panel'>
          <h4>Reconciled</h4>
          {reconciliation?.allocations.map((a) => {
            const b = data.bills.find((b) => b.id === a.billId)
            return (
              <p key={a.billId}>
                {b && (
                  <button className='bank-link' onClick={() => openBill(b)}>
                    {b.billNumber} · {b.contactName}
                  </button>
                )}{' '}
                <strong>{money(a.amount)}</strong> ·{' '}
                {a.createdPayment ? 'Payment recorded here' : 'Existing payment linked'}
              </p>
            )
          })}
          {reconciliation?.scheduleChanges.map((s) => (
            <p key={s.templateId}>
              Next generation changed from {day(s.before.nextDate)} to {day(s.after.nextDate)}.
            </p>
          ))}
          {reconciliation?.billChanges?.map((change) => (
            <p key={change.billId}>
              Bill updated
              {change.after.total !== undefined
                ? `: ${money(change.before.total)} → ${money(change.after.total)}`
                : ''}
              {change.after.date
                ? ` · bill date ${day(change.before.date)} → ${day(change.after.date)}`
                : ''}
              .
            </p>
          ))}
          {reconciliation?.createdTemplateId && (
            <p>A recurring bill template was created for future generations.</p>
          )}
          <p className='bank-muted'>
            Reversing unlinks the bank evidence and reverses payments created here. Bills, bill
            amendments and recurring templates remain in place.
          </p>
          <button
            className='btn btn-sm btn-light-danger'
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  'Reverse this reconciliation? Bills, bill amendments, existing payments and recurring templates will be kept.'
                )
              )
                void run(
                  'reverse',
                  {reconciliationId: t.reconciliationId},
                  'Reconciliation reversed'
                )
            }}
          >
            Reverse reconciliation
          </button>
        </section>
      ) : t.reviewedAt ? (
        <section className='bank-panel'>
          <h4>
            {t.reviewKind === 'dismissed'
              ? 'Dismissed from bill reconciliation'
              : 'Reviewed without a bill'}
          </h4>
          <p>{t.reviewNote}</p>
          {t.reviewKind === 'dismissed' && (
            <p className='bank-muted'>
              This bank entry is still included in Cashflow using its existing classification.
            </p>
          )}
          <button
            className='btn btn-sm btn-light'
            disabled={busy}
            onClick={() =>
              run(
                'review',
                {transactionId: t.id, clear: true, ...(t.reviewId ? {reviewId: t.reviewId} : {})},
                'Returned to review'
              )
            }
          >
            Return to review
          </button>
        </section>
      ) : (
        <>
          {t.amount < 0 && t.category !== 'internal_transfer' && t.scope !== 'personal' ? (
            <>
              <section className='bank-panel'>
                <div className='bank-section-title'>
                  <h4>Find the bill</h4>
                  <span>{matching ? 'Checking matches…' : 'Suggestions need confirmation'}</span>
                </div>
                <input
                  className='form-control'
                  aria-label='Search bills'
                  placeholder='Search supplier, bill number or reference…'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className='bank-matches'>
                  {results.map((b) => {
                    const suggestion = suggestions.find((s) => s.billId === b.id)
                    return (
                      <div className='bank-match' key={b.id}>
                        <div>
                          <button className='bank-link' onClick={() => openBill(b)}>
                            {b.contactName} · {b.billNumber}
                          </button>
                          <small>
                            {search
                              ? `Due ${day(b.dueDate)} · ${b.status}`
                              : suggestion?.reasons.join(' · ')}
                          </small>
                        </div>
                        <strong>
                          {money(b.amountDue || 0)}
                          <small>outstanding</small>
                        </strong>
                        <button
                          className='btn btn-sm btn-light-primary'
                          disabled={busy || !!newBill || allocations.some((a) => a.billId === b.id)}
                          onClick={() => addBill(b, suggestion?.paymentId || '')}
                        >
                          Select
                        </button>
                      </div>
                    )
                  })}
                  {!matching && !results.length && (
                    <p className='bank-muted'>
                      No suggested bill. Search for one or create it from this payment.
                    </p>
                  )}
                </div>
                <button
                  className='btn btn-sm btn-light'
                  disabled={busy}
                  onClick={() => {
                    if (newBill) {
                      setNewBill(null)
                      return
                    }
                    setAllocations([])
                    setSchedules({})
                    setNewBill({
                      contactId: '',
                      contactName: '',
                      description: t.descriptionRaw,
                      reference: '',
                      taxType: '',
                      recurring: false,
                      frequency: 'MONTHLY',
                      nextDate: nextGenerationAfterPayment(t.date, 'MONTHLY', data.asOf),
                    })
                  }}
                >
                  {newBill ? 'Back to existing bills' : 'Create a new bill'}
                </button>
              </section>
              {newBill && (
                <NewBankBillPanel
                  draft={newBill}
                  onChange={setNewBill}
                  amount={-t.amount}
                  paymentDate={t.date}
                  today={data.asOf}
                  busy={busy}
                  existingTemplates={data.templates}
                />
              )}
              {allocations.map((a) => {
                const b = data.bills.find((b) => b.id === a.billId)!
                const payments = unlinkedPayments(b.id)
                return (
                  <section className='bank-panel' key={a.billId}>
                    <div className='bank-section-title'>
                      <h4>
                        {b.contactName} · {b.billNumber}
                      </h4>
                      <button
                        aria-label={`Remove ${b.billNumber}`}
                        className='bank-link'
                        disabled={busy}
                        onClick={() => {
                          setAllocations((v) => v.filter((x) => x.billId !== a.billId))
                          const tplId = b.repeatingTemplateId
                          if (tplId)
                            setSchedules((v) => {
                              const copy = {...v}
                              delete copy[tplId]
                              return copy
                            })
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <label className='bank-field'>
                      Payment
                      <select
                        className='form-select'
                        value={a.paymentId}
                        disabled={busy}
                        onChange={(e) => {
                          const p = payments.find((p) => p.id === e.target.value)
                          update(a.billId, {
                            paymentId: e.target.value,
                            amount: p
                              ? p.amount.toFixed(2)
                              : Math.min(-t.amount, b.amountDue || 0).toFixed(2),
                            acknowledge: false,
                            matchAmount: false,
                            dateChoice: undefined,
                          })
                          if (a.dateChoice === 'align' && b.repeatingTemplateId)
                            setSchedules((v) => {
                              const copy = {...v}
                              delete copy[b.repeatingTemplateId!]
                              return copy
                            })
                        }}
                      >
                        <option value=''>Record a payment from this bank line</option>
                        {payments.map((p) => (
                          <option key={p.id} value={p.id}>
                            Link existing {money(p.amount)} · {day(p.date)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {payments.length > 0 && !a.paymentId && (
                      <label className='bank-check'>
                        <input
                          type='checkbox'
                          checked={a.acknowledge}
                          disabled={busy}
                          onChange={(e) => update(a.billId, {acknowledge: e.target.checked})}
                        />
                        I checked the recorded payments above; this is an additional payment.
                      </label>
                    )}
                    <label className='bank-field'>
                      Amount allocated
                      <input
                        className='form-control'
                        type='number'
                        min='0.01'
                        step='0.01'
                        value={a.amount}
                        disabled={busy || !!a.paymentId}
                        onChange={(e) => update(a.billId, {amount: e.target.value})}
                      />
                    </label>
                    <small className='bank-muted'>
                      {a.paymentId
                        ? 'Links evidence to this payment; no second payment is created.'
                        : `${money(b.amountDue || 0)} outstanding. Partial payment is supported.`}
                    </small>
                    {!a.paymentId &&
                      canMatchBillAmount(b) &&
                      (Math.round(Number(a.amount) * 100) !==
                        Math.round((b.amountDue || 0) * 100) ||
                        a.matchAmount) && (
                        <div className='bank-notice'>
                          <label className='bank-check'>
                            <input
                              type='checkbox'
                              disabled={busy}
                              checked={!!a.matchAmount}
                              onChange={(e) => update(a.billId, {matchAmount: e.target.checked})}
                            />
                            Update bill amount to match this payment
                          </label>
                          {a.matchAmount ? (
                            <p>
                              Bill total {money(b.total)} →{' '}
                              <strong>
                                {money(
                                  (b.amountPaid || 0) + billCredits(b) + Number(a.amount || 0)
                                )}
                              </strong>
                              . Line amounts and tax will be recalculated. This updates this bill
                              only.
                            </p>
                          ) : (
                            <p>
                              The bill keeps its current total. A smaller payment leaves the
                              remaining balance outstanding.
                            </p>
                          )}
                        </div>
                      )}
                    {!a.paymentId && !a.matchAmount && Number(a.amount) > (b.amountDue || 0) && (
                      <p className='bank-error'>
                        Update the bill amount or allocate the excess to another bill.
                      </p>
                    )}
                    {needsDateChoice(a, b) && (
                      <div className='bank-notice'>
                        <h5>Payment date differs from the bill</h5>
                        <p>
                          Bill date {day(b.date)} · Payment {day(t.date)}.
                        </p>
                        <p>
                          Use the payment date for this bill and align future generations? The next
                          bill would be generated{' '}
                          {day(
                            nextGenerationAfterPayment(
                              t.date,
                              data.templates.find((tpl) => tpl.id === b.repeatingTemplateId)!
                                .frequency,
                              data.asOf
                            )
                          )}
                          .
                        </p>
                        <div className='bank-choice-buttons'>
                          <button
                            className='btn btn-sm btn-light'
                            aria-pressed={a.dateChoice === 'align'}
                            disabled={busy}
                            onClick={() => {
                              update(a.billId, {dateChoice: 'align'})
                              const tpl = data.templates.find(
                                (tpl) => tpl.id === b.repeatingTemplateId
                              )!
                              setSchedules((v) => ({
                                ...v,
                                [tpl.id]: nextGenerationAfterPayment(
                                  t.date,
                                  tpl.frequency,
                                  data.asOf
                                ),
                              }))
                            }}
                          >
                            Use payment date
                          </button>
                          <button
                            className='btn btn-sm btn-light'
                            aria-pressed={a.dateChoice === 'keep'}
                            disabled={busy}
                            onClick={() => {
                              update(a.billId, {dateChoice: 'keep'})
                              if (a.dateChoice === 'align')
                                setSchedules((v) => {
                                  const copy = {...v}
                                  delete copy[b.repeatingTemplateId!]
                                  return copy
                                })
                            }}
                          >
                            Keep current dates
                          </button>
                        </div>
                        {a.dateChoice === 'align' && (
                          <small>
                            Bill date becomes {day(t.date)}.{' '}
                            {b.dueDate < t.date
                              ? 'Its due date moves to the payment date too. '
                              : ''}
                            Future generations follow the payment day.
                          </small>
                        )}
                      </div>
                    )}
                  </section>
                )
              })}
              {selectedTemplates.map((tpl) => {
                const next = schedules[tpl.id]
                let dates: string[] = []
                try {
                  if (next && /^\d{4}-\d{2}-\d{2}$/.test(next)) {
                    dates = [next]
                    for (let i = 0; i < 2; i++)
                      dates.push(
                        addFrequency(
                          dates[dates.length - 1],
                          tpl.frequency,
                          allocations.some(
                            (a) =>
                              a.dateChoice === 'align' &&
                              data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId ===
                                tpl.id
                          )
                            ? t.date
                            : next
                        )
                      )
                    dates = dates.filter((d) => !tpl.endDate || d <= tpl.endDate)
                  }
                } catch {
                  /* Invalid input is rejected by server. */
                }
                return (
                  <section className='bank-panel bank-schedule' key={tpl.id}>
                    <h4>Recurring schedule · {tpl.contactName}</h4>
                    <p>
                      {tpl.frequency.toLowerCase()} · Next generation {day(tpl.nextDate)}
                    </p>
                    <label className='bank-check'>
                      <input
                        type='checkbox'
                        disabled={busy || tpl.status !== 'ACTIVE'}
                        checked={next !== undefined}
                        onChange={(e) => {
                          const checked = e.target.checked
                          if (!checked)
                            setAllocations((rows) =>
                              rows.map((a) =>
                                data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId ===
                                tpl.id
                                  ? {...a, dateChoice: 'keep'}
                                  : a
                              )
                            )
                          setSchedules((v) => {
                            const copy = {...v}
                            if (checked) copy[tpl.id] = tpl.nextDate
                            else delete copy[tpl.id]
                            return copy
                          })
                        }}
                      />
                      Update next generation date
                    </label>
                    {tpl.status !== 'ACTIVE' && (
                      <small>
                        Reactivate this template in Recurring Templates to change its schedule.
                      </small>
                    )}
                    {next !== undefined && (
                      <>
                        <label className='bank-field'>
                          Next bill generation
                          <input
                            type='date'
                            className='form-control'
                            value={next}
                            min={data.asOf}
                            max={tpl.endDate || undefined}
                            disabled={busy}
                            onChange={(e) => {
                              setSchedules((v) => ({...v, [tpl.id]: e.target.value}))
                              setAllocations((rows) =>
                                rows.map((a) =>
                                  data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId ===
                                  tpl.id
                                    ? {...a, dateChoice: 'keep'}
                                    : a
                                )
                              )
                            }}
                          />
                        </label>
                        <p className='bank-date-preview'>{dates.map((d) => day(d)).join(' → ')}</p>
                        <small>
                          {allocations.some(
                            (a) =>
                              a.dateChoice === 'align' &&
                              data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId ===
                                tpl.id
                          )
                            ? 'The selected bill uses the payment date. Other existing bills keep their dates.'
                            : 'Future cycles follow this day. Existing bills keep their dates. Payment due dates are separate.'}
                        </small>
                      </>
                    )}
                  </section>
                )
              })}
              {(allocations.length > 0 || newBill) && (
                <div className='bank-confirm'>
                  <div>
                    <strong>{money(total / 100)} allocated</strong>
                    <span>
                      {balanced
                        ? 'Matches the statement amount'
                        : `${money((-t.amount * 100 - total) / 100)} still to allocate`}
                    </span>
                  </div>
                  <button
                    className='btn btn-primary'
                    disabled={
                      busy ||
                      matching ||
                      !!overviewError ||
                      !balanced ||
                      hasUnacknowledged ||
                      unansweredDate ||
                      !!invalidNewBill ||
                      allocations.some(
                        (a) =>
                          !Number.isFinite(Number(a.amount)) ||
                          Number(a.amount) <= 0 ||
                          (!a.paymentId &&
                            !a.matchAmount &&
                            Number(a.amount) >
                              (data.bills.find((b) => b.id === a.billId)?.amountDue || 0))
                      )
                    }
                    onClick={() =>
                      run(
                        'reconcile',
                        {
                          transactionId: t.id,
                          requestId,
                          ...(newBill
                            ? {
                                newBill: {
                                  ...newBill,
                                  amount: -t.amount,
                                  recurring: newBill.recurring
                                    ? {
                                        frequency: newBill.frequency,
                                        nextDate: newBill.nextDate,
                                        alignToPayment:
                                          newBill.nextDate ===
                                          nextGenerationAfterPayment(
                                            t.date,
                                            newBill.frequency,
                                            data.asOf
                                          ),
                                      }
                                    : null,
                                },
                              }
                            : {}),
                          allocations: allocations.map((a) => ({
                            billId: a.billId,
                            amount: Number(a.amount),
                            ...((!a.paymentId && a.matchAmount) || a.dateChoice === 'align'
                              ? {
                                  amendment: {
                                    revision: data.bills.find((b) => b.id === a.billId)!.revision,
                                    matchAmount: !a.paymentId && !!a.matchAmount,
                                    matchDate: a.dateChoice === 'align',
                                  },
                                }
                              : {}),
                            ...(a.paymentId
                              ? {paymentId: a.paymentId}
                              : {
                                  acknowledgedPaymentIds: a.acknowledge
                                    ? unlinkedPayments(a.billId).map((p) => p.id)
                                    : [],
                                }),
                          })),
                          schedules: selectedTemplates
                            .filter((tpl) => schedules[tpl.id] !== undefined)
                            .map((tpl) => ({
                              templateId: tpl.id,
                              nextDate: schedules[tpl.id],
                              revision: tpl.revision,
                              ...(allocations.some(
                                (a) =>
                                  a.dateChoice === 'align' &&
                                  data.bills.find((b) => b.id === a.billId)?.repeatingTemplateId ===
                                    tpl.id
                              )
                                ? {alignToPayment: true}
                                : {}),
                            })),
                        },
                        'Bank transaction reconciled'
                      )
                    }
                  >
                    {busy
                      ? 'Saving…'
                      : newBill
                      ? newBill.recurring
                        ? 'Create bill, recurring template & reconcile'
                        : 'Create bill & reconcile'
                      : allocations.some((a) => a.matchAmount || a.dateChoice === 'align')
                      ? 'Update bill & reconcile'
                      : Object.keys(schedules).length
                      ? 'Reconcile & update schedule'
                      : 'Confirm reconciliation'}
                  </button>
                  {unansweredDate && (
                    <small>
                      Choose whether to use the payment date or keep the current dates above.
                    </small>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className='bank-panel'>
              {t.amount >= 0
                ? 'Incoming money is retained in your cashflow. Review its classification and evidence here; bill matching applies to outgoing payments.'
                : t.scope === 'personal'
                ? 'This transaction belongs to family books. Business bills are excluded.'
                : 'This is classified as an internal transfer. Check the paired account before completing your review.'}
              {t.pairedTransactionId && (
                <small className='d-block'>
                  Linked transfer: {t.pairedTransactionId.slice(0, 12)}…
                </small>
              )}
            </p>
          )}
          <section className='bank-panel bank-secondary'>
            <button className='bank-link' onClick={() => setReviewOpen((v) => !v)}>
              Review without a bill
            </button>
            <button
              className='btn btn-sm btn-light'
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                setError('')
                try {
                  const r = await bankRequest('askAndrei', {transactionId: t.id})
                  toast.info(`Andrei review ${r.status}. Find it in Andrei tasks.`)
                } catch (e: any) {
                  setError(e.message)
                } finally {
                  setBusy(false)
                }
              }}
            >
              Ask Andrei to investigate
            </button>
            {reviewOpen && (
              <div className='bank-review-note'>
                <p>
                  For transfers, personal spending, bank fees or income with evidence already
                  checked. A reviewed item is distinct from a bill reconciliation.
                </p>
                <label className='bank-field'>
                  Category
                  <select
                    className='form-select'
                    value={t.category}
                    disabled={busy}
                    onChange={async (e) => {
                      try {
                        await setCategory(t.id, e.target.value)
                      } catch (e: any) {
                        setError(e.message)
                      }
                    }}
                  >
                    <option value={t.category}>{getCategoryLabel(t.category)}</option>
                    <option value='expense:bank_fees'>Bank fees</option>
                    <option value='expense:other'>Other expense</option>
                    <option value='income:other'>Other income</option>
                    <option value='internal_transfer'>Internal transfer</option>
                  </select>
                </label>
                <label className='bank-field'>
                  Books
                  <select
                    className='form-select'
                    value={t.scope || ''}
                    disabled={busy}
                    onChange={async (e) => {
                      try {
                        await setScope(t.id, (e.target.value || null) as any)
                      } catch (e: any) {
                        setError(e.message)
                      }
                    }}
                  >
                    <option value=''>Unassigned</option>
                    <option value='business'>Business</option>
                    <option value='personal'>Family</option>
                  </select>
                </label>
                <label className='bank-field'>
                  Reason / evidence
                  <textarea
                    className='form-control'
                    value={note}
                    maxLength={1000}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                <button
                  className='btn btn-sm btn-light-primary'
                  disabled={busy || note.trim().length < 5 || t.category === 'uncategorised'}
                  onClick={() => run('review', {transactionId: t.id, note}, 'Transaction reviewed')}
                >
                  Mark reviewed
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

export function ReconcileView() {
  const {
    accounts,
    transactions,
    batches,
    refresh,
    loading,
    refreshing,
    error: bankError,
  } = useCashFlow()
  const reconciliation = useReconciliation()
  const linkedTransaction = new URLSearchParams(window.location.search).get('transaction') || ''
  const [account, setAccount] = useState('all'),
    [month, setMonth] = useState(''),
    [filter, setFilter] = useState(linkedTransaction ? 'all' : 'pending')
  const [direction, setDirection] = useState('all')
  const [checked, setChecked] = useState<string[]>([])
  const dismissal = useBankDismissal()
  const [selected, setSelected] = useState(linkedTransaction),
    [upload, setUpload] = useState(false),
    [bill, setBill] = useState<ReconciliationBill | null>(null)
  const rows = transactions.filter(
    (t) =>
      anzAccount(t.accountId) &&
      (account === 'all' || t.accountId === account) &&
      (!month || t.date.startsWith(month))
  )
  const statusRows = rows.filter(
    (t) =>
      filter === 'all' ||
      (filter === 'pending'
        ? pending(t)
        : filter === 'reconciled'
        ? !!t.reconciliationId
        : !!t.reviewedAt)
  )
  const visible = statusRows.filter(
    (t) => direction === 'all' || (direction === 'out' ? t.amount < 0 : t.amount > 0)
  )
  const current = visible.find((t) => t.id === selected)
  useEffect(() => {
    setChecked([])
  }, [account, month, filter, direction])
  const nextAfter = (ids: string[]) => {
    const position = visible.findIndex((t) => t.id === selected)
    return (
      [...visible.slice(position + 1), ...visible.slice(0, Math.max(0, position))].find(
        (t) => !ids.includes(t.id)
      )?.id || ''
    )
  }
  const dismiss = async (ids: string[]) => {
    const next = nextAfter(ids)
    const previous = selected
    if (await dismissal.dismiss(ids)) {
      setChecked((v) => v.filter((id) => !ids.includes(id)))
      if (filter === 'pending' && ids.includes(previous))
        setSelected((v) => (v === previous ? next : v))
    }
  }
  const afterChange = async (accounting = true) => {
    if (filter === 'pending' && current) setSelected(nextAfter([current.id]))
    if (accounting) await reconciliation.refresh(true)
  }
  const refreshAll = async () => {
    await Promise.all([refresh(), reconciliation.refresh()])
  }
  return (
    <section className='bank-workspace'>
      <header className='bank-heading'>
        <div>
          <span className='bank-eyebrow'>ANZ · Statement reconciliation</span>
          <h2>Connect the payment to the bill</h2>
          <p>Review evidence, settle existing bills and keep recurring schedules in step.</p>
        </div>
        <button className='btn btn-primary' onClick={() => setUpload(true)}>
          Import ANZ CSV
        </button>
      </header>
      {bankError && (
        <p className='bank-error' role='alert'>
          {bankError}
        </p>
      )}
      <div className='bank-toolbar'>
        <label>
          Account
          <select
            className='form-select'
            value={account}
            onChange={(e) => {
              setAccount(e.target.value)
              setSelected('')
            }}
          >
            <option value='all'>All ANZ accounts</option>
            {accounts
              .filter((a) => anzAccount(a.id))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName}
                </option>
              ))}
          </select>
        </label>
        <label>
          Statement month
          <input
            type='month'
            className='form-control'
            value={month}
            onChange={(e) => {
              setMonth(e.target.value)
              setSelected('')
            }}
          />
        </label>
        {month && (
          <button className='bank-link' onClick={() => setMonth('')}>
            All dates
          </button>
        )}
        <button
          className='bank-link'
          disabled={refreshing || reconciliation.refreshing || dismissal.busy}
          onClick={refreshAll}
        >
          {refreshing || reconciliation.refreshing ? 'Updating…' : 'Refresh'}
        </button>
        <a className='bank-link' href='/cashflow'>
          Import history
        </a>
        <span>
          {rows.filter(pending).length} to review · {rows.length} statement lines
        </span>
      </div>
      <div className='bank-account-status'>
        {accounts
          .filter((a) => anzAccount(a.id) && (account === 'all' || a.id === account))
          .map((a) => {
            const dates = batches
              .filter(
                (b) => b.accountId === a.id && !b.reverted && (!b.state || b.state === 'complete')
              )
              .map((b) => b.dateRangeEnd)
              .sort()
            return (
              <span key={a.id}>
                {a.displayName}:{' '}
                {dates.length
                  ? `latest import ${day(dates[dates.length - 1])}`
                  : 'no statement imported'}
              </span>
            )
          })}
        <span>Balance verification unavailable for transaction-only ANZ CSVs.</span>
      </div>
      <div className='bank-status-tabs' role='group' aria-label='Transaction status'>
        {[
          ['pending', 'Needs review'],
          ['reconciled', 'Reconciled'],
          ['reviewed', 'Reviewed'],
          ['all', 'All transactions'],
        ].map(([v, label]) => (
          <button
            key={v}
            aria-pressed={filter === v}
            onClick={() => {
              setFilter(v)
              setSelected('')
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className='bank-direction' role='group' aria-label='Money direction'>
        {[
          ['all', 'All money'],
          ['out', 'Money out'],
          ['in', 'Money in'],
        ].map(([value, label]) => (
          <button
            key={value}
            aria-pressed={direction === value}
            className={value}
            onClick={() => {
              setDirection(value)
              setSelected('')
            }}
          >
            {label}
            <span>
              {
                statusRows.filter(
                  (t) => value === 'all' || (value === 'out' ? t.amount < 0 : t.amount > 0)
                ).length
              }
            </span>
          </button>
        ))}
      </div>
      {dismissal.error && (
        <p className='bank-error' role='alert'>
          {dismissal.error}
        </p>
      )}
      {dismissal.lastDismissal && (
        <div className='bank-dismiss-feedback' role='status'>
          <span>
            {dismissal.lastDismissal.ids.length}{' '}
            {dismissal.lastDismissal.ids.length === 1 ? 'transaction' : 'transactions'} dismissed.
            Kept in Cashflow.
          </span>
          <button className='bank-link' disabled={dismissal.busy} onClick={dismissal.undo}>
            Undo dismissal
          </button>
        </div>
      )}
      <div className='bank-columns'>
        <BankTransactionFeed
          rows={visible}
          accounts={accounts}
          selected={current?.id}
          onSelect={setSelected}
          checked={checked}
          onCheck={setChecked}
          onDismiss={(ids) => {
            void dismiss(ids)
          }}
          busy={dismissal.busy}
          loading={loading}
          hasStatements={!!rows.length}
        />
        <div className='bank-detail-container'>
          {current ? (
            <ReconcileDetail
              key={current.id}
              transaction={current}
              onDone={afterChange}
              openBill={setBill}
            />
          ) : (
            <div className='bank-empty'>
              <i className='bi bi-link-45deg' />
              <h3>Select a statement line</h3>
              <p>Its bill matches, payment history and recurring schedule will appear here.</p>
              <p>Matching records a payment already made. It does not send money.</p>
            </div>
          )}
        </div>
      </div>
      <UploadCsvModal show={upload} onHide={() => setUpload(false)} anzOnly />
      {bill && (
        <BillsProvider>
          <BillViewModal
            show
            bill={bill}
            onHide={() => {
              setBill(null)
              void refreshAll()
            }}
          />
        </BillsProvider>
      )}
    </section>
  )
}
